import { motion } from "framer-motion";
import { ArrowUpRight, Coins, CloudOff, Layers } from "lucide-react";

const FACTS = [
  {
    icon: Coins,
    title: "Pay per query.",
    body: "Pay for the searches you run. No idle compute to keep running.",
  },
  {
    icon: CloudOff,
    title: "Serverless by design.",
    body: "Build search into your product or AI agent without managing search servers.",
  },
  {
    icon: Layers,
    title: "Skip the embedding pipeline.",
    body: "No upfront ingestion or embedding fees. Search your knowledge in natural language.",
  },
];

export const ProductDefinition = () => (
  <section
    id="product"
    data-testid="product-definition"
    className="border-y border-[#233c4d]/25 bg-[#10191f]"
  >
    <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="max-w-3xl"
      >
        <span className="eyebrow">Meet WonderSearch</span>
        <h2 className="mt-4 text-3xl font-medium tracking-tight text-[#f3f5f3] md:text-4xl lg:text-5xl">
          Semantic search.
          <br />
          <span className="text-[#889296]">Without the heavy lifting.</span>
        </h2>
        <p className="mt-5 text-sm leading-relaxed text-[#a7b1b5] md:text-base">
          WonderSearch is a serverless sparse retrieval solution for semantic
          search. Find what people mean, pay per query, and get started without
          upfront ingestion or embedding fees.
        </p>
      </motion.div>

      <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3">
        {FACTS.map((fact, i) => (
          <motion.div
            key={fact.title}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
            data-testid={`fact-${i + 1}`}
            className="rounded-2xl border border-[#233c4d]/30 bg-[#0b100d]/60 p-7"
          >
            <fact.icon className="h-6 w-6 text-[#ff8a4c]" strokeWidth={1.8} aria-hidden="true" />
            <h3 className="mt-4 text-lg font-medium text-[#f3f5f3]">{fact.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-[#889296]">{fact.body}</p>
          </motion.div>
        ))}
      </div>

      <a
        href="https://wondersearch.ai/benchmarks/"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="benchmarks-link"
        className="mt-10 inline-flex items-center gap-2 text-sm font-semibold text-[#edf2f4] transition-colors duration-200 hover:text-[#bce8d1]"
      >
        Explore the retrieval benchmarks
        <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
      </a>
    </div>
  </section>
);
